import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip, Box } from '@mui/material';
import { UI_Table } from './ui-table';

export function SegmentationPopupInterest({ segmentationMain, segmentData, region_mapping }) {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filteredInterests, setFilteredInterests] = useState(segmentData.interest || []);
  
  // Extract unique categories from the interest data
  const categoryOptions = segmentData.interest 
    ? [...new Set(segmentData.interest.map(item => item.category))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)) // Sort alphabetically in ascending order
        .map(category => ({ label: category, value: category }))
    : [];

  // Filter the interest data when search or category selection changes
  useEffect(() => {
    let filtered = segmentData.interest || [];
    
    // Apply keyword search filter
    if (searchKeyword) {
      const searchLower = searchKeyword.toLowerCase();
      filtered = filtered.filter(item => 
        item.keyword?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply category filter
    if (selectedCategories.length > 0) {
      const categoryValues = selectedCategories.map(cat => cat.value);
      filtered = filtered.filter(item => 
        categoryValues.includes(item.category)
      );
    }
    
    setFilteredInterests(filtered);
  }, [searchKeyword, selectedCategories, segmentData.interest]);

  return (
    <div>
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        {/* Search input for keywords */}
        <TextField
          variant="outlined"
          label="Search Interest"
          placeholder="Type to search..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          fullWidth
          sx={{
            "& .MuiInputLabel-root": { fontSize: '12px' },
            "& input": { fontSize: '12px' },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: 'rgba(0, 0, 0, 0.23)',
                borderWidth: '1px',
              },
              "&:hover fieldset": {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              "&.Mui-focused fieldset": {
                borderColor: 'primary.main',
              }
            }
          }}
        />
        
        {/* Category dropdown */}
        <Autocomplete
          multiple
          options={categoryOptions}
          getOptionLabel={(option) => option?.label || ""}
          value={selectedCategories}
          onChange={(event, newValues) => setSelectedCategories(newValues)}
          renderTags={(values, getTagProps) =>
            values.map((option, index) => (
              <Chip
                variant="outlined"
                label={option?.label || option}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Category"
              placeholder={selectedCategories?.length ? "" : "Select categories"}
              sx={{
                "& .MuiInputLabel-root": { fontSize: '12px' },
                "& input": { fontSize: '12px' },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: '1px',
                  },
                  "&:hover fieldset": {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: 'primary.main',
                  }
                }
              }}
            />
          )}
          fullWidth
        />
      </Box>

      <UI_Table 
        rows={filteredInterests} 
        titles={[
          {title: 'Interest', key:'keyword'}, 
          {title: 'Category', key: 'category'}, 
          {title: 'Subcategory', key: 'subcategory'}, 
          {title: 'Interest Percentage', key: 'value', type: 'percentage'}
        ]} 
        maxHeight={500}
      />
    </div>
  );
}